<template>
  <v-dialog v-model="showDialog" width="700" persistent>
    <v-card>
      <v-card-title class="secondary white--text">
        {{ rateDetails.id ? "Edit Rate" : "Add Rate" }}
        <v-spacer></v-spacer>
        <v-btn icon @click="resetForm(), (showDialog = false)">
          <v-icon class="white--text">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-5">
        <v-form ref="rateForm" v-model="isValid" lazy-validation>
          <v-row class="ma-0">
            <v-col cols="12" lg="6" class="Form-Columns-Dense">
              <Autocomplete
                :loading="listLoading"
                :totalItems="laneCount"
                :items="allLaneList"
                @updateList="getLaneList"
                label="Route *"
                itemText="title"
                itemValue="id"
                :rules="[(v) => !!v || 'Route is Required']"
                hide-details="auto"
                v-model="rateDetails.lane"
                :error-messages="formErrors.lane"
                @input="delete formErrors.lane"
              />
            </v-col>
            <v-col cols="12" lg="6" class="Form-Columns-Dense">
              <MultiSelect
                label="Job Type *"
                :items="allJobType"
                itemText="display_name"
                itemValue="value"
                :rules="[(v) => !!v || 'Job Type is Required']"
                hide-details="auto"
                v-model="rateDetails.job_type"
                :error-messages="formErrors.job_type"
                @input="delete formErrors.job_type"
              />
            </v-col>
            <v-col cols="12" lg="6" class="Form-Columns-Dense">
              <MultiSelect
                label="Container Type *"
                :items="allContainersType"
                itemText="type_name"
                itemValue="id"
                :rules="[(v) => !!v || 'Container Type is Required']"
                hide-details="auto"
                v-model="rateDetails.container_type"
                :error-messages="formErrors.container_type"
                @input="delete formErrors.container_type"
              />
            </v-col>
            <v-col cols="12" lg="3" class="Form-Columns-Dense">
              <InputField
                type="number"
                label="Count *"
                min="1"
                max="4"
                :rules="[
                  (v) => !!v || 'Count is Required',
                  (v) => v > 0 || 'Count can not be less than 1',
                  (v) => v < 5 || 'Count can not be more than 4',
                ]"
                hide-details="auto"
                v-model="rateDetails.container_count"
                :error-messages="formErrors.container_count"
                @input="delete formErrors.container_count"
              />
            </v-col>
            <v-col cols="12" lg="3" class="Form-Columns-Dense">
              <InputField
                type="number"
                min="0"
                :label="`Rate (${$globals.currency}) *`"
                required
                :rules="[
                  (val) => !!val || 'Rate is Required',
                  (val) => val > 0 || 'Rate can not be less than or equal to 0',
                ]"
                hide-details="auto"
                v-model="rateDetails.rate"
                :error-messages="formErrors.rate"
                @input="delete formErrors.rate"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-5">
        <v-btn
          :disabled="!isValid"
          class="ml-auto"
          @click="submitForm"
          depressed
          color="primary"
        >
          Submit
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import InputField from "@/components/FormBaseComponents/InputField.vue";
import Autocomplete from "@/components/FormBaseComponents/Autocomplete.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";

export default {
  components: {
    InputField,
    Autocomplete,
    MultiSelect,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    contractId: {
      type: Number,
    },
    editRate: {
      type: Object,
      default: () => ({}),
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.getLaneList();
        this.getContainersType();
        this.getJobTypeOptions();
        this.rateDetails = { ...this.editRate };
        if (this.rateDetails.lane && typeof this.rateDetails.lane != "object") {
          this.getLaneObject(this.rateDetails.lane);
        }
      }
    },
  },
  data() {
    return {
      rateDetails: {},
      formErrors: {},
      allLaneList: [],
      allContainersType: [],
      allJobType: [],
      listLoading: false,
      laneCount: 0,
      isValid: true,
    };
  },
  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  methods: {
    getLaneList(params = {}) {
      this.listLoading = true;
      params = {
        is_active: true,
        offset: 0,
        limit: 10,
        ...params,
      };
      this.$api.lane
        .getLaneList(params)
        .then((res) => {
          this.listLoading = false;
          if ("count" in res) {
            this.laneCount = res.count;
          }
          if (params.offset == 0) {
            this.allLaneList = res.data;
          } else {
            this.allLaneList.push(...res.data);
          }
        })
        .catch(() => {
          this.listLoading = false;
        });
    },
    getLaneObject(id) {
      this.$api.lane
        .getLaneObject(id)
        .then((res) => {
          this.rateDetails.lane = res.data;
        })
        .catch(() => {});
    },
    getContainersType() {
      this.$api.containerType
        .getContainerTypeSelectList({ limit: "all" })
        .then((res) => {
          this.allContainersType = res.data;
        })
        .catch(() => {});
    },
    getJobTypeOptions() {
      this.$api.customerContractV2
        .getCustomerContractV2Options()
        .then((res) => {
          this.allJobType =
            res.data.actions.POST.customer_contract_v2_rates.child.children.job_type.choices;
        })
        .catch(() => {});
    },
    submitForm() {
      this.$bus.$emit("showLoader", true);
      let data = { ...this.rateDetails };
      if (data.lane && typeof data.lane == "object") {
        data.lane = data.lane.id;
      }
      data.customer_contract_v2 = this.contractId;

      const request = data.id
        ? this.$api.customerContractV2.updateCustomerContractV2Rate({
            id: data.id,
            data: data,
          })
        : this.$api.customerContractV2.addCustomerContractV2Rate(data);

      request
        .then(() => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: "Rate saved Successfully!",
            color: "success",
          });
          this.resetForm();
          this.showDialog = false;
          this.$emit("submitted");
        })
        .catch((err) => {
          this.$bus.$emit("showLoader", false);
          const containerTypeError =
            err.data &&
            err.data.container_type &&
            (Array.isArray(err.data.container_type)
              ? err.data.container_type.join()
              : err.data.container_type);
          this.$bus.$emit("showToastMessage", {
            message: `${
              containerTypeError ||
              (err.data && err.data.non_field_errors
                ? err.data.non_field_errors.join()
                : "Couldn't save Rate!")
            }`,
            color: "error",
          });
          this.formErrors = { ...this.formErrors, ...err.data };
        });
    },
    resetForm() {
      if (this.$refs.rateForm) {
        this.$refs.rateForm.reset();
      }
      this.formErrors = {};
      this.rateDetails = {};
    },
  },
};
</script>
